import { useEffect } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { Controller, useFormContext } from "react-hook-form";
import { GET_ADDRESS_TIMEZONE, GET_PRODUCTS } from "../../queries";
import TextField from "@/components/TextField";
import Radio from "@/components/Radio";
import EventTypesDropdown from "../components/EventTypesDropdown";
import { PAT_NATIONAL_FEE_LOOKUP_KEY } from "../components/VariantsDropdown";
import SectionAutocomplete from "../components/SectionAutocomplete";
import UniversityDropdown from "../components/UniversityDropdown";
import FacilityAutocomplete from "../components/FacilityAutocomplete";
import { Facility, type University } from "../types";
import AddressFields from "../components/AddressFields";
import { BACKOFFICE_TIMEZONE, getDefaultRegistrationClosingDate, isSeminarFormEvent } from "../utils";
import { onBlurDialogKeyboard, onFocusDialogKeyboard } from "@/lib/mobileHelpers";

const LOCATION_TYPES = [
  { value: "onsite", label: "Onsite" },
  { value: "virtual", label: "Virtual" }
];

const EventDetailsStep = ({
  hasRegistrations,
  isEditing,
  originalRegistrationCloseDate
}: {
  hasRegistrations: boolean;
  isEditing?: boolean;
  originalRegistrationCloseDate?: any;
}) => {
  const {
    control,
    clearErrors,
    trigger,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext();
  const locationType = watch("locationType");
  const sectionId = watch("sectionId");
  const eventType = watch("eventType");
  const orderTemplateId = watch("orderTemplateId");
  const eventFees = watch("eventFees");
  const sessions = watch("sessions");

  const PAT_NATIONAL_REGISTRATION_PRODUCT_PATH = "products.0";
  const PatNationalRegistrationProductId = watch(`${PAT_NATIONAL_REGISTRATION_PRODUCT_PATH}.id`);
  const isPAT = eventType.name === "PAT";
  const isSeminar = isSeminarFormEvent({ eventType, orderTemplateId, eventFees });
  const isUniversityEvent = eventType.name.toLowerCase().includes("university");
  // When creating a new PAT event, default the registration close date to 7 days prior to the earliest session start date
  useEffect(() => {
    if (!isPAT || isEditing) return;
    setValue(
      "registrationCloseDate",
      getDefaultRegistrationClosingDate(sessions, eventType.name, isEditing, originalRegistrationCloseDate)
    );
  }, [eventType, setValue, sessions, isPAT, isEditing, originalRegistrationCloseDate]);

  useQuery(GET_PRODUCTS, {
    variables: {
      input: { lookupKey: PAT_NATIONAL_FEE_LOOKUP_KEY }
    },
    skip: !isPAT || !!PatNationalRegistrationProductId,
    onCompleted: (data) => {
      if (data?.commerceProducts?.nodes[0]) {
        setValue(`${PAT_NATIONAL_REGISTRATION_PRODUCT_PATH}.id`, data.commerceProducts.nodes[0].id);
        setValue(`${PAT_NATIONAL_REGISTRATION_PRODUCT_PATH}.name`, data.commerceProducts.nodes[0].name);
        setValue(`${PAT_NATIONAL_REGISTRATION_PRODUCT_PATH}.variantName`, data.commerceProducts.nodes[0].description);
      }
    }
  });

  useEffect(() => {
    if (!isPAT) {
      setValue("products", [
        {
          id: "",
          variantId: "",
          name: "",
          variantName: "",
          variantPrice: 0,
          collection: "registration"
        }
      ]);
    }
  }, [setValue, isPAT]);

  useEffect(() => {
    if (!isSeminar) {
      setValue("eventFees", []);
      setValue("orderTemplateId", "");
    }
  }, [isSeminar, setValue]);

  const setAddressFields = (
    address: {
      address1?: string | null;
      address2?: string | null;
      city?: string | null;
      state?: string | null;
      zipcode?: string | null;
    } = {}
  ) => {
    setValue("address1", address.address1 ?? "", { shouldValidate: true });
    setValue("address2", address.address2 ?? "", { shouldValidate: true });
    setValue("city", address.city ?? "", { shouldValidate: true });
    setValue("state", address.state ?? "", { shouldValidate: true });
    setValue("zipcode", address.zipcode ?? "", { shouldValidate: true });
  };

  const handleFacilitySelect = (facility: Facility) => {
    if (facility.addresses.length > 0) {
      const { address1, address2, city, state, postalCode } = facility.addresses[0];
      setAddressFields({ address1, address2, city, state, zipcode: postalCode });
    }
  };

  const handleUniversitiesChange = (universities: University[]) => {
    if (universities.length === 0) {
      setAddressFields();
      return;
    }
    const { address1, address2, city, state, zip } = universities[0].address;
    setAddressFields({ address1, address2, city, state, zipcode: zip });
  };

  const address1 = watch("address1");
  const city = watch("city");
  const state = watch("state");
  const zipcode = watch("zipcode");

  const [getAddressTimezone] = useLazyQuery(GET_ADDRESS_TIMEZONE);

  useEffect(() => {
    if (locationType === "onsite" && address1 && city && state && zipcode) {
      getAddressTimezone({
        variables: { address1, city, state, zip: zipcode },
        onCompleted: (data) => {
          setValue("timezone", data.addressTimezone ?? BACKOFFICE_TIMEZONE);
        },
        onError: () => {
          setValue("timezone", BACKOFFICE_TIMEZONE);
        }
      });
    } else {
      setValue("timezone", BACKOFFICE_TIMEZONE);
    }
  }, [locationType, address1, city, state, zipcode, getAddressTimezone, setValue]);

  useEffect(() => {
    if (locationType === "virtual") {
      setValue("timezone", BACKOFFICE_TIMEZONE);
    }
  }, [locationType, setValue]);

  return (
    <div className="flex flex-col gap-6">
      <p className="text-title-large">Enter details about the event.</p>
      <EventTypesDropdown disabled={hasRegistrations} />

      <Controller
        name="eventName"
        data-testid="event-name-input"
        control={control}
        render={({ field: { onChange, value } }) => (
          <TextField
            label="Event Name"
            className="w-full"
            value={value}
            onChange={(e) => {
              onChange(e);
              if ((e.target as HTMLInputElement).value && errors.eventName) {
                clearErrors("eventName");
              }
              if (!(e.target as HTMLInputElement).value) {
                trigger("eventName");
              }
            }}
            required
            error={!!errors.eventName}
            errorText={errors.eventName?.message as string}
          />
        )}
      />

      <SectionAutocomplete />
      {isUniversityEvent && (
        <UniversityDropdown disabled={hasRegistrations} onUniversitiesChange={handleUniversitiesChange} />
      )}

      {eventType.name !== "PAT" && (
        <>
          <p className="text-title-medium">Event Location</p>
          <Controller
            control={control}
            name="locationType"
            render={({ field }) => (
              <div className="flex flex-col" data-testid="location-type-radio-group">
                {LOCATION_TYPES.map((option, i) => (
                  <div className={`px-6 ${i === 0 ? "pb-3" : "py-3"}`} key={i}>
                    <Radio
                      name={`locationType-${option.value}`}
                      label={option.label}
                      checked={field.value === option.value}
                      onChange={() => {
                        field.onChange(option.value);
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          />
        </>
      )}
      {locationType === "onsite" && (
        <>
          <FacilityAutocomplete sectionId={sectionId} onFacilitySelect={handleFacilitySelect} />
          <AddressFields />
        </>
      )}
      <div>
        <Controller
          name="location"
          data-testid="location-input"
          control={control}
          render={({ field: { onChange, value } }) => (
            <>
              <TextField
                label="Location Details"
                className="w-full"
                value={value}
                onChange={onChange}
                onFocus={(event) => onFocusDialogKeyboard("events", event)}
                onBlur={() => onBlurDialogKeyboard("events")}
              />
              <span className="text-body-small text-sys-brand-on-surface-variant ml-4">
                e.g. Ballroom A, Room 246, Meet at Golf Range, Google Meet: https://...
              </span>
            </>
          )}
        />
      </div>
    </div>
  );
};

export default EventDetailsStep;
